import { useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../services/api';
import SchoolPicker, { renderSchoolLogo } from './SchoolPicker';
import StaffRewardImageUpload from './StaffRewardImageUpload';
import LocationPickerMap from './LocationPickerMap';
import xmarkIcon from '../assets/icons/xmark.svg';

/**
 * Sport schedules for one school: pick a sport, then edit its events.
 *
 * The opponent is normally chosen from the schools we already have, which is
 * what makes the logo automatic — the event links to that school and reads its
 * logo live, so a school that later changes its logo updates every event
 * referencing it. The "not listed" path is the exception, and only that path
 * asks for an upload.
 *
 * Presentation follows PreferredPartnersSection so the tabs on this page read
 * as one surface: white card on #e5e7eb hairline, #f9fafb rows, and the same
 * modal shell.
 */

const card = {
  backgroundColor: 'white',
  padding: '24px',
  borderRadius: '12px',
  marginBottom: '16px',
  border: '1px solid #e5e7eb',
};

const cardTitle = {
  fontFamily: '"Baikal Book"',
  fontSize: '18px',
  fontWeight: 600,
  marginBottom: '20px',
  color: '#111827',
};

const emptyState = {
  textAlign: 'center',
  padding: '40px',
  backgroundColor: '#f9fafb',
  borderRadius: '8px',
  color: '#6b7280',
  fontFamily: '"Baikal Book"',
};

const row = {
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  backgroundColor: '#f9fafb',
  borderRadius: '8px',
  gap: '16px',
};

const ghostButton = {
  padding: '8px 14px',
  backgroundColor: 'white',
  color: '#374151',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  fontFamily: '"Baikal Book"',
  fontSize: '14px',
  cursor: 'pointer',
};

const addPill = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  backgroundColor: 'white',
  color: '#374151',
  border: '1px dashed #9ca3af',
  borderRadius: '999px',
  fontFamily: '"Baikal Book"',
  fontSize: '14px',
  cursor: 'pointer',
};

const newSportInput = {
  padding: '8px 16px',
  width: '180px',
  border: '1px solid #00C8FF',
  borderRadius: '999px',
  fontFamily: '"Baikal Book"',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
};

const fieldLabel = {
  display: 'block',
  marginBottom: '6px',
  fontFamily: '"Baikal Book"',
  fontSize: '14px',
  fontWeight: 500,
  color: '#374151',
};

const fieldInput = {
  width: '100%',
  padding: '12px',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  fontFamily: '"Baikal Book"',
  fontSize: '14px',
  backgroundColor: 'white',
  boxSizing: 'border-box',
};

const twoUp = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '20px',
  marginBottom: '20px',
};

const emptyEvent = () => ({
  opponent_school: null,
  opponent_name: '',
  location_name: '',
  latitude: '',
  longitude: '',
  check_in_enabled: false,
  check_in_radius_m: 150,
  check_in_points: 500,
  is_home: true,
  is_championship: false,
  date: '',
  time: '',
  home_score: '',
  away_score: '',
  logo_files: [],
  existing_logo_url: '',
});

const formatEventWhen = (event) => {
  const when = event.time ? `${event.date} · ${event.time.slice(0, 5)}` : `${event.date} · TBD`;
  return event.location_name ? `${when} · ${event.location_name}` : when;
};

const ScheduleSection = ({ schoolId, schoolName, schools = [] }) => {
  const [sports, setSports] = useState([]);
  const [selectedSportId, setSelectedSportId] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [newSport, setNewSport] = useState('');
  const [addingSport, setAddingSport] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [manualOpponent, setManualOpponent] = useState(false);
  const [form, setForm] = useState(emptyEvent);
  const [eventPhotos, setEventPhotos] = useState([]);
  const addingSportLock = useRef(false);

  // SchoolPicker takes the list as given, so the current school is dropped here
  // rather than inside it.
  const opponentSchools = useMemo(
    () => schools.filter((school) => school.id !== schoolId),
    [schools, schoolId]
  );

  const selectedSport = useMemo(
    () => sports.find((s) => s.id === selectedSportId) || null,
    [sports, selectedSportId]
  );

  const firstFieldError = (value) =>
    Array.isArray(value) ? value[0] : (typeof value === 'string' ? value : '');

  const readError = (err, fallback) => {
    const data = err?.response?.data;
    return (
      firstFieldError(data?.sport) ||
      firstFieldError(data?.non_field_errors) ||
      data?.detail ||
      data?.error ||
      data?.opponent_logo ||
      err?.message ||
      fallback
    );
  };

  const loadSports = async () => {
    if (!schoolId) {
      setSports([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const list = await apiService.getSportSchedules(schoolId);
      setSports(list);
      // Open on a schedule rather than an empty page. Keeps the current
      // selection when it survived the reload, so adding or deleting a sport
      // doesn't yank the view elsewhere; a deselect (clicking the active pill)
      // doesn't reload, so it stays deselected.
      setSelectedSportId((current) =>
        current && list.some((sport) => sport.id === current)
          ? current
          : list[0]?.id ?? null
      );
    } catch (err) {
      setError(readError(err, 'Failed to load schedules'));
      setSports([]);
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async (scheduleId) => {
    if (!scheduleId) {
      setEvents([]);
      return;
    }
    setLoadingEvents(true);
    try {
      setEvents(await apiService.getScheduleEvents(scheduleId));
    } catch (err) {
      setError(readError(err, 'Failed to load events'));
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  useEffect(() => {
    loadSports();
    setSelectedSportId(null);
    setEvents([]);
  }, [schoolId]);

  useEffect(() => {
    loadEvents(selectedSportId);
  }, [selectedSportId]);

  const handleAddSport = async () => {
    const name = newSport.trim();
    if (!name || addingSportLock.current) return;
    addingSportLock.current = true;
    setSaving(true);
    setError('');
    try {
      const created = await apiService.createSportSchedule(schoolId, name);
      setNewSport('');
      setAddingSport(false);
      setSuccess(`Added ${created.sport}`);
      await loadSports();
      setSelectedSportId(created.id);
    } catch (err) {
      setError(readError(err, 'Failed to add sport'));
    } finally {
      addingSportLock.current = false;
      setSaving(false);
    }
  };

  const handleDeleteSport = async (sport) => {
    if (!window.confirm(`Delete ${sport.sport} and all ${sport.event_count || 0} of its events?`)) return;
    try {
      await apiService.deleteSportSchedule(sport.id);
      if (selectedSportId === sport.id) setSelectedSportId(null);
      await loadSports();
    } catch (err) {
      setError(readError(err, 'Failed to delete sport'));
    }
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setFormError('');
  };

  const openCreate = () => {
    setForm(emptyEvent());
    setEventPhotos([]);
    setManualOpponent(false);
    setEditingId(null);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (event) => {
    const linked = event.opponent_school
      ? schools.find((s) => s.id === event.opponent_school) || null
      : null;
    setForm({
      opponent_school: linked,
      opponent_name: event.opponent_name || '',
      location_name: event.location_name || '',
      latitude: event.latitude ?? '',
      longitude: event.longitude ?? '',
      check_in_enabled: !!event.check_in_enabled,
      check_in_radius_m: event.check_in_radius_m ?? 150,
      check_in_points: event.check_in_points ?? 500,
      is_home: event.is_home !== false,
      is_championship: !!event.is_championship,
      date: event.date || '',
      time: (event.time || '').slice(0, 5),
      home_score: event.home_score ?? '',
      away_score: event.away_score ?? '',
      logo_files: [],
      existing_logo_url: event.opponent_logo_url || '',
    });
    setManualOpponent(!linked);
    setEditingId(event.id);
    setFormError('');
    setShowForm(true);
    apiService
      .getScheduleEventPhotos(event.id)
      .then((data) => setEventPhotos(Array.isArray(data?.photos) ? data.photos : []))
      .catch(() => setEventPhotos([]));
  };

  // The logo already on the event. Rendered through `extraPreviewItems` so it
  // sits in the same preview strip as a freshly dropped file, and removing it
  // is what frees the single slot for a replacement.
  const savedLogoPreview = form.existing_logo_url
    ? [
        <div key="saved-logo" className="image-preview-item">
          <div className="preview-image-wrapper">
            <img src={form.existing_logo_url} alt="" className="preview-image" />
            <button
              type="button"
              className="remove-image-btn"
              onClick={() => setForm((prev) => ({ ...prev, existing_logo_url: '' }))}
            >
              <img src={xmarkIcon} alt="Remove" />
            </button>
          </div>
          <div className="preview-name">Current logo</div>
        </div>,
      ]
    : null;

  const handleSaveEvent = async (e) => {
    e?.preventDefault?.();
    const opponentName = manualOpponent
      ? form.opponent_name.trim()
      : form.opponent_school?.name || '';
    if (!opponentName || !form.date) {
      setFormError('An opponent and a date are required.');
      return;
    }
    if ((form.home_score === '') !== (form.away_score === '')) {
      setFormError('Enter both final scores, or leave both blank.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        schedule: selectedSportId,
        opponent_name: opponentName,
        location_name: form.location_name.trim(),
        latitude: form.latitude === '' ? null : form.latitude,
        longitude: form.longitude === '' ? null : form.longitude,
        check_in_enabled: form.check_in_enabled,
        check_in_radius_m: form.check_in_radius_m === '' ? 150 : form.check_in_radius_m,
        check_in_points: form.check_in_points === '' ? 500 : form.check_in_points,
        is_home: !!form.is_home,
        is_championship: !!form.is_championship,
        date: form.date,
        time: form.time || null,
        home_score: form.home_score === '' ? null : form.home_score,
        away_score: form.away_score === '' ? null : form.away_score,
        // A linked school owns the logo; a file is only sent for the manual path.
        opponent_school: manualOpponent ? null : form.opponent_school?.id || null,
      };
      // No new file means the server never touches the logo, so a removal has
      // to be stated outright or the old URL survives the save.
      if (manualOpponent && form.logo_files.length === 0) {
        payload.opponent_logo_url = form.existing_logo_url || '';
      }
      await apiService.saveScheduleEvent(
        payload,
        manualOpponent ? form.logo_files[0] || null : null,
        editingId
      );
      setSuccess(editingId ? 'Event updated' : 'Event added');
      setShowForm(false);
      await loadEvents(selectedSportId);
      await loadSports();
    } catch (err) {
      setFormError(readError(err, 'Failed to save event'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (event) => {
    if (!window.confirm(`Delete the event vs ${event.opponent_name}?`)) return;
    try {
      await apiService.deleteScheduleEvent(event.id);
      await loadEvents(selectedSportId);
      await loadSports();
    } catch (err) {
      setError(readError(err, 'Failed to delete event'));
    }
  };

  return (
    <div>
      {error && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#991b1b',
            marginBottom: '16px',
            fontFamily: '"Baikal Book"',
            fontSize: '14px',
          }}
        >
          {String(error)}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#dcfce7',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            color: '#16a34a',
            marginBottom: '16px',
            fontFamily: '"Baikal Book"',
            fontSize: '14px',
          }}
        >
          {success}
        </div>
      )}

      {/* Step 1 — the sport */}
      <div style={card}>
        <h4 style={cardTitle}>Sports</h4>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px', fontFamily: '"Baikal Book"', color: '#6b7280' }}>
            Loading…
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            {sports.map((sport) => {
              const active = sport.id === selectedSportId;
              return (
                <div
                  key={sport.id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: active ? '#e0f7ff' : '#f9fafb',
                    border: `1px solid ${active ? '#00C8FF' : '#e5e7eb'}`,
                    borderRadius: '999px',
                    overflow: 'hidden',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setSelectedSportId(active ? null : sport.id)}
                    style={{
                      padding: '8px 6px 8px 16px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      fontWeight: active ? 500 : 400,
                      color: active ? '#0369a1' : '#374151',
                    }}
                  >
                    {sport.sport}
                    <span style={{ color: '#6b7280', marginLeft: '8px', fontSize: '12px' }}>
                      {sport.event_count ?? 0}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteSport(sport)}
                    title={`Delete ${sport.sport}`}
                    style={{
                      padding: '8px 12px 8px 6px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#9ca3af',
                      fontSize: '16px',
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            })}

            {addingSport ? (
              <input
                type="text"
                autoFocus
                style={newSportInput}
                value={newSport}
                placeholder="Sport name"
                disabled={saving}
                onChange={(e) => setNewSport(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                  if (e.key === 'Escape') {
                    setNewSport('');
                    setAddingSport(false);
                  }
                }}
                // Clicking away saves what was typed rather than discarding it.
                onBlur={() => {
                  if (newSport.trim()) handleAddSport();
                  else setAddingSport(false);
                }}
              />
            ) : (
              <button type="button" style={addPill} onClick={() => setAddingSport(true)}>
                <span style={{ fontSize: '16px', lineHeight: 1 }}>+</span>
                Add Sport
              </button>
            )}
          </div>
        )}
      </div>

      {/* Step 2 — its events */}
      {selectedSport && (
        <div style={card}>
          <h4 style={cardTitle}>{selectedSport.sport} Schedule</h4>

          {loadingEvents ? (
            <div style={{ textAlign: 'center', padding: '20px', fontFamily: '"Baikal Book"', color: '#6b7280' }}>
              Loading events…
            </div>
          ) : events.length === 0 ? (
            <div style={emptyState}>No events on this schedule yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {events.map((event) => (
                <div key={event.id} style={row}>
                  {renderSchoolLogo({ image: event.logo_url }, 50)}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: '"Baikal Book"',
                        fontSize: '16px',
                        fontWeight: 500,
                        marginBottom: '4px',
                      }}
                    >
                      vs {event.opponent_name}
                    </div>
                    <div
                      style={{
                        fontFamily: '"Baikal Book"',
                        fontSize: '12px',
                        color: '#6b7280',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <span>{formatEventWhen(event)}</span>
                      {event.opponent_school_name && (
                        <span
                          style={{
                            padding: '2px 8px',
                            backgroundColor: '#e0f7ff',
                            color: '#0369a1',
                            borderRadius: '999px',
                          }}
                        >
                          Linked school
                        </span>
                      )}
                      {event.home_score != null && event.away_score != null && (
                        <span
                          style={{
                            padding: '2px 8px',
                            backgroundColor: '#ecfdf5',
                            color: '#047857',
                            borderRadius: '999px',
                          }}
                        >
                          Final {event.home_score}–{event.away_score}
                        </span>
                      )}
                      {event.latitude != null && event.longitude != null && (
                        <span
                          style={{
                            padding: '2px 8px',
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            borderRadius: '999px',
                          }}
                        >
                          {Number(event.latitude).toFixed(4)}, {Number(event.longitude).toFixed(4)}
                        </span>
                      )}
                    </div>
                  </div>
                  <button type="button" style={ghostButton} onClick={() => openEdit(event)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    style={{ ...ghostButton, color: '#dc2626', borderColor: '#fecaca' }}
                    onClick={() => handleDeleteEvent(event)}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            className="staff-page-button"
            onClick={openCreate}
            style={{ marginTop: '20px' }}
          >
            <span className="staff-page-button-text">Add Event</span>
          </button>
        </div>
      )}

      {showForm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: 1002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeForm();
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '720px',
              maxHeight: 'calc(100vh - 48px)',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 18px',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <div
                style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '16px',
                  fontWeight: 900,
                  color: '#111827',
                }}
              >
                {editingId ? 'Edit Event' : 'Add Event'} — {selectedSport?.sport}
              </div>
              <button
                type="button"
                onClick={closeForm}
                disabled={saving}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '20px',
                  lineHeight: 1,
                  padding: '6px 8px',
                  color: '#111827',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                ×
              </button>
            </div>

            <form
              onSubmit={handleSaveEvent}
              style={{ padding: '24px', overflowY: 'auto', flex: 1, minHeight: 0 }}
            >
              {formError && (
                <div
                  style={{
                    padding: '12px 14px',
                    backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    color: '#991b1b',
                    marginBottom: '20px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                  }}
                >
                  {String(formError)}
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label style={fieldLabel}>Opponent *</label>
                {manualOpponent ? (
                  <>
                    <input
                      type="text"
                      style={fieldInput}
                      value={form.opponent_name}
                      onChange={(e) => setForm({ ...form, opponent_name: e.target.value })}
                      placeholder="Opponent name"
                    />
                    <StaffRewardImageUpload
                      inputId="schedule-opponent-logo"
                      maxImages={1}
                      existingCount={form.existing_logo_url ? 1 : 0}
                      extraPreviewItems={savedLogoPreview}
                      images={form.logo_files}
                      setImages={(update) =>
                        setForm((prev) => ({
                          ...prev,
                          logo_files:
                            typeof update === 'function' ? update(prev.logo_files) : update,
                        }))
                      }
                    />
                    <button
                      type="button"
                      style={ghostButton}
                      onClick={() => {
                        setManualOpponent(false);
                        setForm({ ...form, opponent_name: '', logo_files: [], existing_logo_url: '' });
                      }}
                    >
                      Choose a school instead
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <SchoolPicker
                          schools={opponentSchools}
                          value={form.opponent_school}
                          placeholder="Select opponent school"
                          onChange={(school) => setForm({ ...form, opponent_school: school })}
                        />
                      </div>
                      <button
                        type="button"
                        className="staff-page-button"
                        style={{ flexShrink: 0, height: '56px', padding: '0 20px' }}
                        onClick={() => {
                          setManualOpponent(true);
                          setForm({ ...form, opponent_school: null });
                        }}
                      >
                        <span className="staff-page-button-text">Add School</span>
                      </button>
                    </div>
                    <p
                      style={{
                        margin: '6px 0 0',
                        fontFamily: '"Baikal Book"',
                        fontSize: '12px',
                        color: '#6b7280',
                      }}
                    >
                      Their logo is filled in automatically. Use Add for an opponent not in our system.
                    </p>
                  </>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={fieldLabel}>Location name</label>
                <input
                  type="text"
                  style={fieldInput}
                  value={form.location_name}
                  onChange={(e) => setForm({ ...form, location_name: e.target.value })}
                  placeholder="e.g. Nippert Stadium"
                />
              </div>

              <div style={twoUp}>
                <div>
                  <label style={fieldLabel}>Latitude</label>
                  <input
                    type="text"
                    style={fieldInput}
                    value={form.latitude}
                    onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                    placeholder="39.131400"
                  />
                </div>
                <div>
                  <label style={fieldLabel}>Longitude</label>
                  <input
                    type="text"
                    style={fieldInput}
                    value={form.longitude}
                    onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                    placeholder="-84.516800"
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    cursor: 'pointer',
                    marginBottom: '8px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!form.is_home}
                    onChange={(e) => setForm({ ...form, is_home: e.target.checked })}
                  />
                  Home game
                </label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    cursor: 'pointer',
                    marginBottom: '8px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!form.is_championship}
                    onChange={(e) => setForm({ ...form, is_championship: e.target.checked })}
                  />
                  Championship game
                </label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.check_in_enabled}
                    onChange={(e) => setForm({ ...form, check_in_enabled: e.target.checked })}
                  />
                  Allow check-in at this event
                </label>
                {form.check_in_enabled && (
                  <div style={{ ...twoUp, marginTop: '12px', marginBottom: 0 }}>
                    <div>
                      <label style={fieldLabel}>Check-in radius (m)</label>
                      <input
                        type="number"
                        min="1"
                        style={fieldInput}
                        value={form.check_in_radius_m}
                        onChange={(e) => setForm({ ...form, check_in_radius_m: e.target.value })}
                        placeholder="150"
                      />
                    </div>
                    <div>
                      <label style={fieldLabel}>Check-in points</label>
                      <input
                        type="number"
                        min="0"
                        style={fieldInput}
                        value={form.check_in_points}
                        onChange={(e) => setForm({ ...form, check_in_points: e.target.value })}
                        placeholder="500"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <LocationPickerMap
                  latitude={form.latitude}
                  longitude={form.longitude}
                  radiusMeters={form.check_in_enabled ? form.check_in_radius_m : null}
                  onPick={({ lat, lng }) =>
                    setForm((prev) => ({
                      ...prev,
                      latitude: String(lat),
                      longitude: String(lng),
                    }))
                  }
                />
              </div>

              {editingId && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={fieldLabel}>Game photos</label>
                  {eventPhotos.length === 0 ? (
                    <p style={{ margin: 0, fontFamily: '"Baikal Book"', fontSize: '13px', color: '#6b7280' }}>
                      No photos uploaded for this game yet.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {eventPhotos.map((photo) => (
                        <div
                          key={photo.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '10px 12px',
                            backgroundColor: photo.is_winner ? '#ecfdf5' : '#f9fafb',
                            borderRadius: '8px',
                            border: photo.is_winner ? '1px solid #a7f3d0' : '1px solid #e5e7eb',
                          }}
                        >
                          {photo.image_url ? (
                            <img
                              src={photo.image_url}
                              alt=""
                              style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px' }}
                            />
                          ) : null}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: '"Baikal Book"', fontSize: '13px', color: '#111827' }}>
                              {photo.email || photo.user_id}
                            </div>
                            {photo.is_winner ? (
                              <div style={{ fontFamily: '"Baikal Book"', fontSize: '12px', color: '#065f46' }}>
                                Winning photo
                              </div>
                            ) : null}
                          </div>
                          {!photo.is_winner && (
                            <button
                              type="button"
                              style={ghostButton}
                              onClick={async () => {
                                try {
                                  await apiService.markScheduleEventPhotoWinner(editingId, photo.id);
                                  const data = await apiService.getScheduleEventPhotos(editingId);
                                  setEventPhotos(Array.isArray(data?.photos) ? data.photos : []);
                                  setSuccess('Winning photo saved.');
                                } catch (err) {
                                  setFormError(readError(err, 'Failed to mark winning photo'));
                                }
                              }}
                            >
                              Mark winner
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={twoUp}>
                <div>
                  <label style={fieldLabel}>Date *</label>
                  <input
                    type="date"
                    style={fieldInput}
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                  />
                </div>
                <div>
                  <label style={fieldLabel}>Time</label>
                  <input
                    type="time"
                    style={fieldInput}
                    value={form.time}
                    onChange={(e) => setForm({ ...form, time: e.target.value })}
                  />
                  <p style={{ margin: '6px 0 0', fontFamily: '"Baikal Book"', fontSize: '12px', color: '#6b7280' }}>
                    Leave blank for TBD.
                  </p>
                </div>
              </div>

              <div style={twoUp}>
                <div>
                  <label style={fieldLabel}>
                    Final score{schoolName ? ` — ${schoolName}` : ' — home'}
                  </label>
                  <input
                    type="number"
                    min="0"
                    style={fieldInput}
                    value={form.home_score}
                    onChange={(e) => setForm({ ...form, home_score: e.target.value })}
                    placeholder="—"
                  />
                </div>
                <div>
                  <label style={fieldLabel}>
                    Final score — {manualOpponent ? (form.opponent_name.trim() || 'opponent') : (form.opponent_school?.name || 'opponent')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    style={fieldInput}
                    value={form.away_score}
                    onChange={(e) => setForm({ ...form, away_score: e.target.value })}
                    placeholder="—"
                  />
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #e5e7eb',
                  paddingTop: '20px',
                }}
              >
                <button type="button" style={ghostButton} onClick={closeForm} disabled={saving}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="staff-page-button"
                  style={{ opacity: saving ? 0.6 : 1 }}
                  disabled={saving}
                >
                  <span className="staff-page-button-text">{saving ? 'Saving…' : 'Save Event'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleSection;
